import type { PropsWithChildren, ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { tv } from "tailwind-variants";

import { icons } from "./icons";

const modalVariants = tv({
  slots: {
    overlay: [
      "fixed inset-0 z-50 bg-overlay backdrop-blur-sm",
      "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
      "data-[state=open]:animate-in data-[state=open]:fade-in-0",
    ],
    content: [
      "fixed top-1/2 left-1/2 z-50 -translate-x-1/2 -translate-y-1/2",
      "grid w-[calc(100%-2rem)] max-w-lg gap-4 rounded-xl",
      "border border-line bg-surface-raised p-6 text-content shadow-overlay",
      "duration-200 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
    ],
    close: [
      "absolute top-4 right-4 inline-flex size-8 cursor-pointer items-center justify-center rounded-md",
      "text-content-subtle transition-colors duration-150",
      "hover:bg-surface-hover hover:text-content",
      "focus:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2 focus-visible:ring-offset-surface-raised focus-visible:outline-none",
    ],
  },
});

const { close, content, overlay } = modalVariants();

/*
  Deliberately no max-height or overflow here. A scroll container on the dialog
  clips any in-flow popover a child renders (the role select in the admin modals),
  and the alternative — letting Headless UI portal the panel to the body — cannot
  work inside a Radix modal dialog: Radix handles Escape on the document in the
  CAPTURE phase and always leaves the event defaultPrevented, while Headless UI's
  mergeProps skips its own handlers on a defaultPrevented event, so the panel
  could never be closed with the keyboard.
*/

type ModalProps = {
  className?: string;
  show: boolean;
  title: ReactNode;
  description?: string;
  onClose: () => void;
};

export const Modal = ({
  children,
  className,
  description,
  onClose,
  show,
  title,
}: PropsWithChildren<ModalProps>) => {
  return (
    <Dialog.Root
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      open={show}
    >
      <Dialog.Portal>
        <Dialog.Overlay className={overlay()} />

        <Dialog.Content className={content({ className })}>
          <div className="flex flex-col gap-1.5 pr-8">
            <Dialog.Title className="text-base leading-6 font-semibold text-content-strong">
              {title}
            </Dialog.Title>

            {description ? (
              <Dialog.Description className="text-sm text-content-muted">
                {description}
              </Dialog.Description>
            ) : null}
          </div>

          {children}

          <Dialog.Close className={close()}>
            <icons.XMarkIcon aria-hidden="true" className="size-5" />

            <span className="sr-only">Close</span>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
